import { CurrencyRateListItem } from 'src/app/shared/currency';
import { minfinMbPoint } from 'src/app/services/external-api.service';

/** What the Minfin button does with one returned point. */
export type MinfinPlanAction = 'write' | 'skip-no-currency' | 'skip-bad-value' | 'skip-same-date' | 'skip-unchanged';

export interface MinfinPlanItem {
  curcode: string;
  action: MinfinPlanAction;
  /** Set for `write`: the row to post. */
  row?: CurrencyRateListItem;
  /** Log line for the panel. */
  message: string;
  /** Warning only (AQ2 not recorded as approved): the row is not owned by MINFIN, but the write is not blocked. */
  warning?: string;
}

/** Local midnight of a `YYYY-MM-DD` day, like the NBU button's `new Date(y, m, d)`; never the browser clock. */
export function dayFromIso(iso: string): Date {
  const [y, m, d] = iso.split('-').map(n => parseInt(n, 10));
  return new Date(y, m - 1, d);
}

/**
 * True when a stored `CRDATE` is the given calendar day. `HttpClient` hands the date over as an ISO string
 * (`2026-10-20T00:00:00.000Z`), not a `Date`, and a browser east of UTC reads that instant as a different local day or time,
 * so comparing timestamps with a local midnight can miss an existing row and post a duplicate. A row counts as that day
 * when either its UTC calendar day or its local calendar day is the day (a false match only skips a write; a miss duplicates).
 */
export function sameCalendarDay(value: Date | string, iso: string): boolean {
  const d = new Date(value);
  if (isNaN(d.getTime())) { return false; }
  const pad = (n: number) => String(n).padStart(2, '0');
  const utc = `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
  const local = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  return utc === iso || local === iso;
}

/**
 * Decides, for one point, what to do. Same rules as the NBU button (`onGetExtApiRate1`): skip a day that already has a
 * row, skip an unchanged value (compared with the newest row), otherwise write. The currency is found by `CURCODE`
 * only; there is no mapping in the client.
 *
 * @param curcode - WebYTO currency code the API keyed the point by.
 * @param point - The point the API returned.
 * @param rateDate - `YYYY-MM-DD`, the Kyiv day from the API response.
 * @param curr - The `Currencies` row with that `CURCODE`, or undefined.
 * @param rates - Existing rows of that currency, newest first (as the NBU button reads them).
 */
export function planMinfinPoint(
  curcode: string,
  point: minfinMbPoint,
  rateDate: string,
  curr: CurrencyRateListItem | undefined,
  rates: Array<Pick<CurrencyRateListItem, 'CRDATE' | 'CRVALUE'>>
): MinfinPlanItem {
  if (!curr) {
    return { curcode, action: 'skip-no-currency', message: `${curcode}: no currency with this code, skipped` };
  }
  const value = point ? point.value : NaN;
  if (typeof value !== 'number' || !isFinite(value) || value <= 0) {
    return { curcode, action: 'skip-bad-value', message: `${curcode}: the answer has no positive value, skipped` };
  }
  const warning = curr.APINAME && curr.APINAME !== 'MINFIN'
    ? `${curcode}: APINAME is "${curr.APINAME}", not MINFIN (warning only)`
    : (!curr.APINAME ? `${curcode}: APINAME is empty, not MINFIN (warning only)` : undefined);
  const day = dayFromIso(rateDate);
  if (rates.some(r => sameCalendarDay(r.CRDATE, rateDate))) {
    return { curcode, action: 'skip-same-date', warning, message: `${curcode}: a rate for ${rateDate} already exists, skipped` };
  }
  if (rates.length > 0 && rates[0].CRVALUE === value) {
    return { curcode, action: 'skip-unchanged', warning, message: `${curcode}: value ${value} is unchanged, skipped` };
  }
  return {
    curcode, action: 'write', warning,
    row: { ...curr, CRDATE: day, CRVALUE: value },
    message: `${curcode}: ${rateDate} = ${value} (slot ${point.slot})` // url-ok: log text, not a URL
  };
}
